import React, { useState, useEffect } from 'react';
import {
  Heart,
  Calendar,
  MapPin,
  Camera,
  Music,
  Gift,
  ChevronDown,
  Menu,
  X,
  ArrowLeft,
  ArrowRight,
} from 'lucide-react';

function EternalElegance() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isParallaxVisible, setIsParallaxVisible] = useState(false);

  const photos = [
    'https://images.unsplash.com/photo-1519225421980-715cb0215aed?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80',
    'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80',
    'https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80',
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsParallaxVisible(scrollPosition > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % photos.length);
  const prevSlide = () =>
    setCurrentSlide((prev) => (prev - 1 + photos.length) % photos.length);

  return (
    <div className="reddit-sans min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed z-50 w-full bg-white/90 shadow-sm backdrop-blur-sm">
        <div className="mx-auto max-w-6xl px-4">
          <div className="flex h-16 items-center justify-between">
            <a href="#" className="font-serif text-xl">
              S & M
            </a>

            {/* Desktop Navigation */}
            <div className="hidden space-x-8 md:flex">
              <a href="#story" className="text-gray-600 hover:text-rose-600">
                Our Story
              </a>
              <a href="#details" className="text-gray-600 hover:text-rose-600">
                Details
              </a>
              <a href="#schedule" className="text-gray-600 hover:text-rose-600">
                Schedule
              </a>
              <a href="#gallery" className="text-gray-600 hover:text-rose-600">
                Gallery
              </a>
              <a href="#rsvp" className="font-medium text-rose-600">
                RSVP
              </a>
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="border-t bg-white md:hidden">
            <div className="space-y-1 px-2 pb-3 pt-2">
              <a
                href="#story"
                className="block px-3 py-2 text-gray-600 hover:text-rose-600"
              >
                Our Story
              </a>
              <a
                href="#details"
                className="block px-3 py-2 text-gray-600 hover:text-rose-600"
              >
                Details
              </a>
              <a
                href="#schedule"
                className="block px-3 py-2 text-gray-600 hover:text-rose-600"
              >
                Schedule
              </a>
              <a
                href="#gallery"
                className="block px-3 py-2 text-gray-600 hover:text-rose-600"
              >
                Gallery
              </a>
              <a
                href="#rsvp"
                className="block px-3 py-2 font-medium text-rose-600"
              >
                RSVP
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <div
        className="relative h-screen bg-cover bg-center"
        style={{
          backgroundImage:
            'url("https://images.unsplash.com/photo-1519741497674-611481863552?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80")',
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
          <Heart className="mb-6 h-12 w-12 animate-pulse" />
          <h1 className="mb-4 text-center font-serif text-6xl">
            Sarah & Michael
          </h1>
          <p className="text-2xl font-light">Are getting married</p>
          <p className="mt-4 text-xl">August 15, 2024</p>
          <button className="mt-12 animate-bounce">
            <ChevronDown className="h-8 w-8" />
          </button>
        </div>
      </div>

      {/* Our Story Section */}
      <div id="story" className="bg-rose-50 px-4 py-20">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="mb-8 font-serif text-4xl">Our Story</h2>
          <p className="mb-12 leading-relaxed text-gray-600">
            We met on a sunny afternoon in Central Park, both reaching for the
            same coffee cup by mistake. What started as an awkward moment turned
            into hours of conversation, laughter, and the beginning of our
            beautiful journey together. Two years later, on the same park bench
            where we first talked, Michael got down on one knee and asked Sarah
            to spend forever with him.
          </p>
          <div className="grid gap-8 md:grid-cols-2">
            <img
              src="https://images.unsplash.com/photo-1634729108619-3576b0db8090?q=80&w=3087&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              alt="Wedding Moment 5"
              className="rounded-lg shadow-lg"
            />
            <img
              src="https://images.unsplash.com/photo-1583939003579-730e3918a45a?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
              alt="Engagement"
              className="rounded-lg shadow-lg"
            />
          </div>
        </div>
      </div>

      {/* Tiled Gallery Section */}
      <div className="py-20">
        <div className="mx-auto max-w-6xl px-4">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            <div className="h-64 md:h-96">
              <img
                src="https://images.unsplash.com/photo-1583939003579-730e3918a45a?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                alt="Wedding Moment 6"
                className="h-full w-full rounded-lg object-cover"
              />
            </div>
            <div className="h-64 md:h-96">
              <img
                src="https://images.unsplash.com/photo-1511285560929-80b456fea0bc?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                alt="Wedding Moment 2"
                className="h-full w-full rounded-lg object-cover"
              />
            </div>
            <div className="h-64 md:h-96">
              <img
                src="https://images.unsplash.com/photo-1519225421980-715cb0215aed?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                alt="Wedding Moment 3"
                className="h-full w-full rounded-lg object-cover"
              />
            </div>
            <div className="h-64 md:h-96">
              <img
                src="https://images.unsplash.com/photo-1469371670807-013ccf25f16a?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                alt="Wedding Moment 4"
                className="h-full w-full rounded-lg object-cover"
              />
            </div>
            <div className="h-64 md:h-96">
              <img
                src="https://images.unsplash.com/photo-1522673607200-164d1b6ce486?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                alt="Wedding Moment 5"
                className="h-full w-full rounded-lg object-cover"
              />
            </div>
            <div className="h-64 md:h-96">
              <img
                src="https://images.unsplash.com/photo-1583939003579-730e3918a45a?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                alt="Wedding Moment 6"
                className="h-full w-full rounded-lg object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Details Section */}
      <div id="details" className="mx-auto max-w-6xl px-4 py-20">
        <div className="grid gap-12 md:grid-cols-3">
          <div className="text-center">
            <Calendar className="mx-auto mb-4 h-12 w-12 text-rose-600" />
            <h3 className="mb-2 font-serif text-2xl">When</h3>
            <p className="text-gray-600">August 15, 2024</p>
            <p className="text-gray-600">4:00 PM</p>
          </div>
          <div className="text-center">
            <MapPin className="mx-auto mb-4 h-12 w-12 text-rose-600" />
            <h3 className="mb-2 font-serif text-2xl">Where</h3>
            <p className="text-gray-600">The Grand Plaza</p>
            <p className="text-gray-600">123 Wedding Lane</p>
            <p className="text-gray-600">New York, NY</p>
          </div>
          <div className="text-center">
            <Heart className="mx-auto mb-4 h-12 w-12 text-rose-600" />
            <h3 className="mb-2 font-serif text-2xl">Celebration</h3>
            <p className="text-gray-600">Dinner & Dancing</p>
            <p className="text-gray-600">to follow ceremony</p>
          </div>
        </div>
      </div>

      {/* Parallax Section */}
      <div
        className="relative flex h-96 items-center justify-center bg-cover bg-fixed bg-center"
        style={{
          backgroundImage:
            'url("https://images.unsplash.com/photo-1469371670807-013ccf25f16a?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80")',
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-40" />
        <div className="relative text-center text-white">
          <p className="font-serif text-3xl">"Love is patient, love is kind"</p>
          <p className="mt-4 italic">1 Corinthians 13:4</p>
        </div>
      </div>

      {/* Schedule Section */}
      <div id="schedule" className="bg-rose-50 px-4 py-20">
        <div className="mx-auto max-w-4xl">
          <h2 className="mb-12 text-center font-serif text-4xl">
            Wedding Day Schedule
          </h2>
          <div className="space-y-8">
            <div className="flex items-center space-x-6">
              <Camera className="h-8 w-8 flex-shrink-0 text-rose-600" />
              <div>
                <p className="font-semibold">4:00 PM - Wedding Ceremony</p>
                <p className="text-gray-600">Join us as we exchange vows</p>
              </div>
            </div>
            <div className="flex items-center space-x-6">
              <Music className="h-8 w-8 flex-shrink-0 text-rose-600" />
              <div>
                <p className="font-semibold">5:30 PM - Cocktail Hour</p>
                <p className="text-gray-600">Enjoy drinks and hors d'oeuvres</p>
              </div>
            </div>
            <div className="flex items-center space-x-6">
              <Gift className="h-8 w-8 flex-shrink-0 text-rose-600" />
              <div>
                <p className="font-semibold">6:30 PM - Reception</p>
                <p className="text-gray-600">
                  Dinner, dancing, and celebrations
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Photo Gallery Section */}
      <div id="gallery" className="px-4 py-20">
        <h2 className="mb-12 text-center font-serif text-4xl">Our Moments</h2>
        <div className="relative mx-auto max-w-6xl">
          <div className="relative h-[600px] overflow-hidden rounded-lg">
            <img
              src={photos[currentSlide]}
              alt={`Photo ${currentSlide + 1}`}
              className="h-full w-full object-cover transition-opacity duration-500"
            />
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-2 hover:bg-white"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-2 hover:bg-white"
            >
              <ArrowRight className="h-6 w-6" />
            </button>
          </div>
          <div className="mt-4 flex justify-center space-x-2">
            {photos.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-3 w-3 rounded-full ${
                  currentSlide === index ? 'bg-rose-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* RSVP Section */}
      <div id="rsvp" className="bg-rose-50 px-4 py-20">
        <div className="mx-auto max-w-md">
          <h2 className="mb-8 text-center font-serif text-4xl">RSVP</h2>
          <form className="space-y-6">
            <div>
              <label className="mb-2 block text-gray-700" htmlFor="name">
                Full Name
              </label>
              <input
                type="text"
                id="name"
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-rose-500"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="mb-2 block text-gray-700" htmlFor="email">
                Email
              </label>
              <input
                type="email"
                id="email"
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-rose-500"
                placeholder="your@email.com"
              />
            </div>
            <div>
              <label className="mb-2 block text-gray-700">
                Will you be attending?
              </label>
              <div className="space-x-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="attending"
                    className="text-rose-600"
                  />
                  <span className="ml-2">Joyfully Accept</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="attending"
                    className="text-rose-600"
                  />
                  <span className="ml-2">Regretfully Decline</span>
                </label>
              </div>
            </div>
            <button
              type="submit"
              className="w-full rounded-lg bg-rose-600 px-6 py-3 text-white transition-colors hover:bg-rose-700"
            >
              Send RSVP
            </button>
          </form>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-50 py-8 text-center text-gray-600">
        <p className="mb-2">#SarahAndMichael</p>
        <p className="text-sm">
          Made with <Heart className="inline h-4 w-4 text-rose-600" /> for our
          special day by Tailwine Studio
        </p>
      </footer>
    </div>
  );
}

export default EternalElegance;
